import type { ApiClient } from '@carbonwebmail/api-client'
import type { SendingDomainDto } from '@carbonwebmail/contracts'
import { useCallback } from 'react'
import type { AsyncState } from '../../ui/Async.js'
import { useAction } from '../../ui/useAction.js'
import { useLoaded } from '../../ui/useLoaded.js'

/**
 * The domains an organisation sends from.
 *
 * Every action reloads rather than patching the row in place. A check is three
 * network calls that can take seconds and can come back `unknown`, and guessing
 * the new state here would put a tick on screen that the server never agreed
 * to — which is the one thing this screen must not do.
 */

export type SendingDomainsState = AsyncState<readonly SendingDomainDto[]>

export function useSendingDomains({ client }: { readonly client: ApiClient }) {
  // Unwrapped here rather than on the screen: the envelope is a fact about the
  // response, and a view that had to reach through it would be the only thing
  // in the product that knew about it.
  const load = useCallback(async () => (await client.sendingDomains()).domains, [client])
  const { state, reload } = useLoaded<readonly SendingDomainDto[]>(
    load,
    'We could not load your sending domains. Please try again in a moment.',
  )

  const { problem, busy, run } = useAction(reload)

  const add = useCallback(
    (domain: string, dkimSelector: string) =>
      run(async () => {
        await client.addSendingDomain({
          domain,
          ...(dkimSelector.trim() === '' ? {} : { dkimSelector }),
        })
        // Nothing to announce either way. A domain that was already there and
        // one just added look identical in the list the reload is about to
        // show, which is the honest answer to both.
        return undefined
      }, 'That does not look like a domain we can check. Use just the domain, with no scheme or path.'),
    [client, run],
  )

  const check = useCallback(
    (domain: string) =>
      run(async () => {
        await client.checkSendingDomain(domain)
        // The answer is the row, which the reload is about to show. A sentence
        // saying "checked" beside a table that now says what it found would be
        // the same news twice.
        return undefined
      }, 'We could not check that domain just now. Try again in a moment.').then(() => undefined),
    [client, run],
  )

  const remove = useCallback(
    (domain: string) =>
      run(async () => {
        await client.deleteSendingDomain(domain)
        // A row that has plainly gone needs no announcement.
        return undefined
      }, 'We could not remove that domain. Please try again in a moment.').then(() => undefined),
    [client, run],
  )

  return { state, problem, busy, add, check, remove, retry: reload }
}
